import { useState, useEffect } from 'react';
import Step1CompleteId from './Step1CompleteId';
import Step2Address from './Step2Address';
import Step3Experience from './Step3Experience';
import Step4Photo from './Step4Photo';
import Step5Certifications from './Step5Certifications';
import Step0SectorSelection from './Step0SectorSelection';
import { auth, db } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

const steps = [
  { id: 'sector', labelKey: 'onboarding.sector.title', component: Step0SectorSelection },
  { id: 'id', labelKey: 'onboarding.identity.title', component: Step1CompleteId },
  { id: 'address', labelKey: 'onboarding.address.title', component: Step2Address },
  { id: 'experience', labelKey: 'onboarding.skills.title', component: Step3Experience },
  { id: 'photo', labelKey: 'onboarding.profilePhoto.title', component: Step4Photo },
  { id: 'certifications', labelKey: 'onboarding.certifications.title', component: Step5Certifications }
];

const OnBoardingFlow = () => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkCompletedSteps = async () => {
      if (!auth.currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Cambiar idioma según la preferencia del usuario
          if (userData.language && i18n.language !== userData.language) {
            i18n.changeLanguage(userData.language);
          }
          
          // Verificar si el usuario ya completó el onboarding
          if (userData.onBoarding) {
            setCompleted(true);
            setTimeout(() => {
              navigate('/worker-dashboard');
            }, 1200);
            return;
          }

          // Verificar steps completados
          const completedSteps = userData.completedSteps || [];
          const nextStepIndex = steps.findIndex(step => !completedSteps.includes(step.id));
          
          if (nextStepIndex !== -1) {
            setCurrentStep(nextStepIndex);
          } else {
            // Si todos los steps están completados, finalizar el onboarding
            await updateDoc(doc(db, 'users', auth.currentUser.uid), { 
              onBoarding: true, 
              role: 'worker', 
              status: 'pending' 
            });
            setCompleted(true);
            setTimeout(() => {
              navigate('/worker-dashboard');
            }, 1200);
          }
        }
      } catch (error) {
        logger.error('OnBoardingFlow', 'Error al verificar steps completados', error);
      }
    };

    checkCompletedSteps();
  }, [navigate, i18n]);

  const handleStepComplete = async (stepId: string) => {
    if (!auth.currentUser) return;

    try {
      // Actualizar el documento del usuario con el step completado
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const completedSteps = userData.completedSteps || [];
        
        if (!completedSteps.includes(stepId)) {
          await updateDoc(userRef, {
            completedSteps: [...completedSteps, stepId]
          });
        }
      }

      // Avanzar al siguiente step o finalizar
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Finalizar el onboarding
        await updateDoc(doc(db, 'users', auth.currentUser.uid), { 
          onBoarding: true, 
          role: 'worker', 
          status: 'pending' 
        });
        setCompleted(true);
        setTimeout(() => {
          navigate('/worker-dashboard');
        }, 1200);
      }
    } catch (error) {
      logger.error('OnBoardingFlow', 'Error al completar step', error);
    }
  };

  const StepComponent = steps[currentStep].component;

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('onboarding.completed')}</h2>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 font-['Montserrat'] flex items-center justify-center">
      <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn">
        <h1 className="text-3xl font-bold text-red-600 mb-8 text-center">{t('onboarding.title') || 'OnBoarding'}</h1>
        <StepComponent onComplete={handleStepComplete} />
      </div>
    </div>
  );
};

export default OnBoardingFlow; 